import {
    namespace, Logger, Point, Matrix,
} from '../../../../../FrontendBundle/Resources/js/ofcs';
import AbstractItem from './abstractItem';

namespace('Ofcs.Editor.Item');

/**
 * @class RectangleItem
 * @extends AbstractItem
 */
export default window.Ofcs.Editor.Item.RectangleItem = AbstractItem.extend({

    type: 'rectangle',

    /**
     * Constant for minimum height and width of a rectangle bag
     *
     * @constant
     * @property {Number} MIN_SIZE
     */
    MIN_SIZE: 5,

    /**
     * This was enabled due to bug. Rectangles have a larger margin in their corners, so depending on which item is
     * leading in collision detection collisions might be detected or not. To mitigate this rectangles should always be
     * checked in bidirectional to ensure the result is always the same. See OFCS-6503 for more information.
     */
    supportsBidirectionalCollisions: true,

    /**
     * Triggered when the segments are updated
     *
     * @event updatesegments
     * @param {CircleItem} item Me
     */

    defaults: {
        /**
         * @property {String} Type
         */
        type: 'Rectangle',

        /**
         * Current position
         * @property {Point} attributes.position
         */
        position: new Point(0, 0),

        /**
         * Current rotation
         * @property {Number} attributes.rotation
         */
        rotation: 0,

        /**
         * Width of the shape
         * @property {Number} attributes.width
         */
        width: 0,

        /**
         * Height of the shape
         * @property {Number} attributes.height
         */
        height: 0,

        /**
         * Depth of the rectangle bag
         * @property {Number} attributes.depth
         */
        depth: 10,

        /**
         * Label
         * @property {String} attributes.userLabel
         */
        userLabel: '',
    },

    initialize() {
        AbstractItem.prototype.initialize.call(this);

        this.supportsWidth = true;
        this.supportsHeight = true;
        this.supportsRotation = true;
        this.supportsPositioning = true;
        this.supportsDepth = true;
        this.supportsCollisions = true;
        this.supportsOutOfFoam = true;
        this.supportsGuides = true;

        // Initialize paths
        this.set('ext', this.calculateSegments(0));
        this.set('orig', this.get('ext'));
        this.set('safety', this.calculateSegments(this.getSafetyMargin()));

        this.on('change', this.onItemChange, this);
    },

    /**
     * Return safety margin for this item
     *
     * @return {number}
     */
    getSafetyMargin() {
        const len = Math.max(this.get('width'), this.get('height'));

        if (!this.hasParentItem()) return 0;

        return this.getParentItem().getSafetyMarginByDepth(this.get('depth'), len);
    },

    onItemChange(item) {
        const changes = item.changed;

        if (changes.width !== undefined || changes.height !== undefined) {
            this.set('ext', this.calculateSegments(0));
            this.set('orig', this.get('ext'));
        }

        if (changes.width !== undefined || changes.height !== undefined || changes.depth !== undefined) {
            this.set('safety', this.calculateSegments(this.getSafetyMargin()));
            this.trigger('updatesegments', this);
        }
    },

    calculateSegments(spread) {
        const width = this.get('width') + (spread * 2);
        const height = this.get('height') + (spread * 2);
        const path = [
            ['M', -width / 2, -height / 2],
            ['L', width / 2, -height / 2],
            ['L', width / 2, height / 2],
            ['L', -width / 2, height / 2],
            ['L', -width / 2, -height / 2],
        ];

        return [path];
    },

    set(key, value, optionsParam) {
        let attrs = key;

        // Handle both `"key", value` and `{key: value}` -style arguments.
        if (typeof key !== 'object') {
            attrs = { [key]: value };
        }

        if (attrs.width !== undefined) {
            attrs.width = Math.max(this.MIN_SIZE, attrs.width);
            Logger.debug('rectangle: fixed width', attrs.width);
        }
        if (attrs.height !== undefined) {
            attrs.height = Math.max(this.MIN_SIZE, attrs.height);
            Logger.debug('rectangle: fixed height', attrs.height);
        }
        if (attrs.depth !== undefined) {
            attrs.depth = Math.max(this.getMinDepth(), attrs.depth);
            attrs.depth = Math.min(this.getMaxDepth(), attrs.depth);
            Logger.debug('rectangle: fixed depth', attrs.depth);
        }

        return AbstractItem.prototype.set.call(this, key, value, optionsParam);
    },

    /**
     * Get top-middle point respect rotation.
     *
     * @return {Point}
     */
    getPointTop() {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const height = this.get('height');
        const point = new Point(center.x, center.y - (height / 2));

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    /**
     * Get bottom-middle point respect rotation.
     *
     * @return {Point}
     */
    getPointBottom() {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const height = this.get('height');
        const point = new Point(center.x, center.y + (height / 2));

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    /**
     * Get middle-left point respect rotation.
     *
     * @return {Point}
     */
    getPointLeft() {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const width = this.get('width');
        const point = new Point(center.x - (width / 2), center.y);

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    /**
     * Get middle-right point respect rotation.
     *
     * @return {Point}
     */
    getPointRight() {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const width = this.get('width');
        const point = new Point(center.x + (width / 2), center.y);

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    /**
     * Get top-left point respect rotation.
     *
     * @return {Point}
     */
    getPointTopLeft() {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const width = this.get('width');
        const height = this.get('height');
        const point = new Point(center.x - (width / 2), center.y - (height / 2));

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    /**
     * Get top-right point respect rotation.
     *
     * @return {Point}
     */
    getPointTopRight() {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const width = this.get('width');
        const height = this.get('height');
        const point = new Point(center.x + (width / 2), center.y - (height / 2));

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    /**
     * Get bottom-left point respect rotation.
     *
     * @return {Point}
     */
    getPointBottomLeft() {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const width = this.get('width');
        const height = this.get('height');
        const point = new Point(center.x - (width / 2), center.y + (height / 2));

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    /**
     * Get bottom-right point respect rotation.
     *
     * @return {Point}
     */
    getPointBottomRight() {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const width = this.get('width');
        const height = this.get('height');
        const point = new Point(center.x + (width / 2), center.y + (height / 2));

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    /**
     * Get minimum with.
     *
     * @return {number}
     */
    getMinWidth() {
        return this.MIN_SIZE;
    },

    /**
     * Get minimum height.
     *
     * @return {number}
     */
    getMinHeight() {
        return this.MIN_SIZE;
    },

    /**
     * Get minimum depth defined in parent item.
     *
     * @return {number}
     */
    getMinDepth() {
        if (this.hasParentItem()) {
            return this.getParentItem().get('minDepth');
        }

        return 0;
    },

    /**
     * Get maximum depth defined in parent item.
     *
     * @return {number}
     */
    getMaxDepth() {
        if (this.hasParentItem()) {
            return this.getParentItem().get('depth');
        }

        return 9999;
    },

    /**
     * Translate object into json stringable types. This method will be used
     * by JSON.stringify() automaticly.
     *
     * @return {Object}
     */
    toJSON(...args) {
        const attributes = AbstractItem.prototype.toJSON.apply(this, args);

        return {
            depth: attributes.depth,
            x: attributes.x,
            y: attributes.y,
            rotation: attributes.rotation,
            width: attributes.width,
            height: attributes.height,
            type: attributes.type,
        };
    },

    /**
     * Initializes item handles
     *
     * @param {Ofcs.Editor.Svg.Canvas} canvas - svg canvas instance
     * @return {Ofcs.Editor.Svg.Handle.ResizeRectangleHandle}
     */
    initializeHandles(canvas) {
        return new Ofcs.Editor.Svg.Handle.ResizeRectangleHandle(this, canvas);
    },
});
